import { fieldConfig } from "@autoform/zod";
import * as z from "zod";
import { COSMETIC_COMPONENTS_SMARTPHONE, COSMETIC_COMPONENTS_SMARTWATCH } from "../common/enums";

// ---------------------------------------------------------------------------
// Cosmetic Components
// ---------------------------------------------------------------------------

const cosmeticComponentsSchema = z.object({
	smartphone: z
		.array(z.enum(COSMETIC_COMPONENTS_SMARTPHONE))
		.default([
			"self-device-turns-on",
			"self-front-cosmetics",
			"self-back-cosmetics",
			"self-body-frame-cosmetics",
			"self-camera-cosmetics",
		])
		.check(
			fieldConfig({
				fieldType: "multiselect",
				description: "The supported smartphone components",
			}),
		)
		.describe("Smartphone Components"),
	smartwatch: z
		.array(z.enum(COSMETIC_COMPONENTS_SMARTWATCH))
		.default(["self-charger", "self-device-material", "self-overall-condition", "self-strap"])
		.check(
			fieldConfig({
				fieldType: "multiselect",
				description: "The supported smartwatch components",
			}),
		)
		.describe("Smartwatch Components"),
});

// ---------------------------------------------------------------------------
// Offer Section — Extra Info
// ---------------------------------------------------------------------------

const contactSchema = z.object({
	email: z.string().optional().describe("Email"),
	contactUrl: z
		.string()
		.optional()
		.check(fieldConfig({ fieldType: "url", description: "The contact form url" }))
		.describe("Contact Form URL"),
});

const extraInfoSectionSchema = z.object({
	displayExtraInfo: z.boolean().default(false).describe("Display Extra Info"),
	storeLocator: z
		.string()
		.optional()
		.check(
			fieldConfig({
				fieldType: "url",
				description: "The partner's store locator",
			}),
		)
		.describe("Store Locator"),
	phoneNumber: z.string().optional().describe("Phone Number"),
	contact: contactSchema.prefault({}).describe("Contact Section"),
});

// ---------------------------------------------------------------------------
// Offer Section
// ---------------------------------------------------------------------------

const offerSectionSchema = z.object({
	// Left empty, the offer page hands the price to the embedding host and shows no button on the partner's own site.
	button: z
		.enum(["find-store", "courier"])
		.nullish()
		.check(
			fieldConfig({
				fieldType: "singleselect",
				description: "The single button offered under the price",
			}),
		)
		.describe("Button"),
	// Smartphone customers confirm their device by IMEI after the price, before the button's next step.
	imeiValidation: z.boolean().default(false).describe("IMEI Validation"),
	detailedReport: z.boolean().default(false).describe("Detailed Report"),
	extraInfoSection: extraInfoSectionSchema.prefault({}).describe("Extra Info Section"),
});

// ---------------------------------------------------------------------------
// Pricing App Schema
// ---------------------------------------------------------------------------

export const pricingappShape = {
	cosmeticComponents: cosmeticComponentsSchema.prefault({}).describe("Cosmetic Components"),
	offerSection: offerSectionSchema.prefault({}).describe("Offer Section"),
	preferredBrandSorting: z.string().optional().describe("Brand Sorting"),
	logoRedirectUrl: z
		.string()
		.default("")
		.check(fieldConfig({ fieldType: "url" }))
		.describe("Logo Redirect URL"),
};

export const pricingappSchema = z.object(pricingappShape);

export type PricingappConfig = z.infer<typeof pricingappSchema>;
